import { useForm } from '@tanstack/react-form';
import { Alert, Button, Card, Form, Input, Page, RadioSet } from '@ui/themed';
import { t } from 'i18next';
import { featureEnabled, features } from '../../../../constants/features';
import { useEnv } from '../../../../context/env-context';
import { useHandlers, useSite, useUser, withPageProviders } from '../../../../context/page-context';
import type { TeamRoleApiResponse } from '../../types';
import { useSettingsTeam } from '../hooks/use-account-team';
import { useInviteTeamMember } from '../hooks/use-invite-member';
import { inviteMemberFormSchema, type TeamSettingsInviteFormValues } from '../schemas';

const TEAM_PAGE_PATH = '/settings/team';

const roleOptions: { label: string; description: string; value: TeamRoleApiResponse }[] = [
  {
    label: t('features:account.pages.invite_member_page.roles.full.label'),
    description: t('features:account.pages.invite_member_page.roles.full.description'),
    value: 'admin',
  },
  {
    label: t('features:account.pages.invite_member_page.roles.restricted.label'),
    description: t('features:account.pages.invite_member_page.roles.restricted.description'),
    value: 'external',
  },
];

export const SettingsTeamInviteFormPage = withPageProviders(SettingsTeamInviteFormPageContent);

function SettingsTeamInviteFormPageContent() {
  const inviteMember = useInviteTeamMember();
  const { notificationHandler, navigationHandler } = useHandlers();
  const { data } = useSettingsTeam();
  const { isSparkStaff, manageAccount } = useUser();
  const { userId } = useSite();
  const { team } = useEnv();

  const isOwner = data ? userId === data.owner.id || !!isSparkStaff : false;
  const rolesEnabled = featureEnabled(features.TEAM_MEMBER_ROLE, team.slugged_name);

  const defaultValues: TeamSettingsInviteFormValues = { email: '', role: 'admin' };

  const form = useForm({
    defaultValues,
    validators: { onSubmit: inviteMemberFormSchema },
    onSubmit: async ({ value }) => {
      const payload = inviteMemberFormSchema.parse(value);

      try {
        await inviteMember.mutateAsync(payload, {
          onSuccess: () => {
            notificationHandler.success(t('notifications:saved'));
            navigationHandler.navigate(TEAM_PAGE_PATH, { excludeSiteEnv: true });
          },
        });
      } catch {
        notificationHandler.error();
      }
    },
  });

  // Only the team owner can invite members.
  if (data && !(manageAccount && isOwner)) {
    return (
      <Page
        narrow
        title={t('features:account.pages.invite_member_page.title')}
        backUrl={TEAM_PAGE_PATH}
        backUrlOpts={{ excludeSiteEnv: true }}
      >
        <Alert variant="warning" headingLevel="h4">
          {t('features:account.pages.invite_member_page.no_access')}
        </Alert>
      </Page>
    );
  }

  return (
    <Page
      narrow
      title={t('features:account.pages.invite_member_page.title')}
      backUrl={TEAM_PAGE_PATH}
      backUrlOpts={{ excludeSiteEnv: true }}
    >
      {data && (
        <Form
          onSubmit={async () => {
            // Clear errors so we can re-validate
            form.setErrorMap({ onSubmit: { fields: {} } });

            await form.handleSubmit();
          }}
        >
          <Card>
            <Card.Header headingLevel="h2">
              {t('features:account.pages.invite_member_page.form_header')}
            </Card.Header>
            <Card.Body>
              <form.Field name="email">
                {(field) => (
                  <Input
                    label={t('placeholders:input.email.label')}
                    placeholder={t('placeholders:input.email.placeholder')}
                    value={field.state.value}
                    onChange={field.handleChange}
                    error={field.state.meta.errors[0]?.message}
                  />
                )}
              </form.Field>
              {rolesEnabled && (
                <form.Field name="role">
                  {(field) => (
                    <RadioSet
                      label={t('features:account.pages.invite_member_page.role_label')}
                      value={field.state.value}
                      options={roleOptions}
                      onChange={field.handleChange}
                    />
                  )}
                </form.Field>
              )}
            </Card.Body>
          </Card>

          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigationHandler.navigate(TEAM_PAGE_PATH, { excludeSiteEnv: true })}
            >
              {t('buttons:cancel')}
            </Button>
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" disabled={inviteMember.isPending || isSubmitting}>
                  {t('buttons:continue')}
                </Button>
              )}
            </form.Subscribe>
          </div>
        </Form>
      )}
    </Page>
  );
}
